import isPropValid from "@emotion/is-prop-valid";
import { Box, lighten, Stack, TextField, Typography, useTheme } from "@mui/material";
import moment from "moment-timezone";
import { Controller, useFormContext } from "react-hook-form";
import ScheduleSelector from "react-schedule-selector";
import { StyleSheetManager } from "styled-components";

import { PROVIDER_REGISTRATION_TITLE_PROPS } from "~/utils/providerRegistrationHelpers";

const Availability = () => {
  const theme = useTheme();

  const { control, register } = useFormContext();

  return (
    <Stack direction="column" spacing={4}>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          What is the maximum number of hours per week you are willing to work?
        </Typography>
        <Controller
          control={control}
          defaultValue=""
          name="availability.maxHoursWeek"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...register("availability.maxHoursWeek", {
                required: "Hours per week is required",
                min: {
                  value: 1,
                  message: "Hours per week must be at least 1",
                },
                max: {
                  value: 168,
                  message: "Hours per week cannot exceed 168",
                },
                valueAsNumber: true,
              })}
              {...field}
              error={!!error}
              helperText={error ? error.message : null}
              label="Hours Per Week"
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes(".")) {
                  e.target.value = value.split(".")[0];
                }
                field.onChange(e);
              }}
              onKeyDown={(e) => {
                if (e.key === "." || e.key === ",") {
                  e.preventDefault();
                }
              }}
              slotProps={{
                htmlInput: {
                  step: 1,
                  min: 1,
                  max: 168,
                  pattern: "\\d*",
                },
              }}
              type="number"
              required
            />
          )}
        />
      </Stack>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          Please indicate your general availability during the week:
        </Typography>
        <Typography align="left" variant="body2">
          Click to select one time or drag to select multiple times at once. Please scroll down to view later times.
        </Typography>
        <Controller
          control={control}
          defaultValue={[]}
          name="availability.generalAvailability"
          render={({ field }) => (
            <Box
              sx={{
                "& > div:first-of-type > div:first-of-type > div:first-of-type": {
                  position: "sticky",
                  top: 0,
                  backgroundColor: "background.paper",
                },
              }}
            >
              <StyleSheetManager shouldForwardProp={(prop) => isPropValid(prop)}>
                <ScheduleSelector
                  hourlyChunks={2}
                  hoveredColor={lighten(theme.palette.primary.main, 0.4)}
                  maxTime={24}
                  minTime={0}
                  numDays={7}
                  onChange={(newSchedule) => {
                    field.onChange(newSchedule);
                  }}
                  renderDateLabel={(date) => (
                    <Typography
                      position="sticky"
                      sx={{
                        backgroundColor: "background.paper",
                      }}
                      textAlign="center"
                      top={0}
                      variant="h6"
                    >
                      {moment(date).format("dddd")}
                    </Typography>
                  )}
                  renderTimeLabel={(time) => <Typography textAlign="end">{moment(time).format("h:mm a")}</Typography>}
                  selectedColor={theme.palette.primary.main}
                  selection={field.value}
                  startDate={new Date(1769990400000)} // schedule will show Sunday-Saturday
                  timeFormat="h:mma"
                  unselectedColor={lighten(theme.palette.primary.light, 0.5)}
                />
              </StyleSheetManager>
            </Box>
          )}
        />
      </Stack>
    </Stack>
  );
};

export default Availability;
